import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../../../core/common/api";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import useTableSearch from "../../../shared/useTableSearch";

const RANKS = ["Leader", "Director", "Partner", "Royalty"] as const;
type Rank = typeof RANKS[number];
type Achiever = {
  _id: string;
  name: string;
  memberId: string;
  club: number;
  rank: Rank;
  sponsorId?: string;
  phone?: string;
};
type Result = {
  users: Achiever[];
  stats: { totalAchievers: number; ranks: Record<Rank, number> };
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

const RewardAchievers = () => {
  const [filters, setFilters] = useState({ search: "", rank: "all", page: 1, limit: 10 });
  const search = useTableSearch(filters.search);
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ search, rank: filters.rank, page: String(filters.page), limit: String(filters.limit) });
    const load = async () => {
      try {
        const response = await apiRequest(`/users/reward-achievers?${params}`, "GET");
        if (!response.success) throw new Error(response.message || "Unable to load reward achievers");
        if (!cancelled) setResult(response);
      } catch (err: unknown) {
        if (!cancelled) {
          setResult(null);
          setError(err instanceof Error ? err.message : "Unable to load reward achievers");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [search, filters.rank, filters.page, filters.limit]);

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MLM</div>
          <h1 className="page-title">Reward <em>Achievers</em></h1>
          <div className="page-sub">Registered members who have reached Leader, Director, Partner or Royalty. Showing each member’s current rank.</div>
        </div>
      </div>
      <section className="metrics-grid">
        {RANKS.map((rank, index) => (
          <MetricCard key={rank} metric={{ label: rank, value: loading ? "…" : result?.stats.ranks[rank] ?? 0, icon: ["🏅", "⭐", "🤝", "👑"][index], tone: ["blue", "green", "orange", "red"][index], helper: "Matching registered members" }} />
        ))}
      </section>
      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input aria-label="Search reward achievers" placeholder="Search name, member ID, email or phone (4+ characters)" value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value, page: 1 })} />
        </div>
        <select className="filter-select" aria-label="Filter by rank" value={filters.rank} onChange={(event) => setFilters({ ...filters, rank: event.target.value, page: 1 })}>
          <option value="all">All ranks</option>
          {RANKS.map((rank) => <option key={rank} value={rank}>{rank}</option>)}
        </select>
        <ResetButton onClick={() => setFilters({ ...filters, search: "", rank: "all", page: 1 })} />
      </div>
      <section className="table-card">
        <div className="table-head">
          <h2>Reward Achievers{!loading && result ? ` (${result.stats.totalAchievers})` : ""}</h2>
          <p>Current ranks of registered members. Monthly profit eligibility is available on Profit Share.</p>
        </div>
        <div className="table-scroll">
          <table className="users-table">
            <thead><tr><th>User</th><th>Member ID</th><th>Club</th><th>Rank</th><th>Sponsor</th><th>Phone</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="empty-state">Loading reward achievers...</td></tr>
                : error ? <tr><td colSpan={7} className="empty-state" role="alert">{error}</td></tr>
                : !result?.users.length ? <tr><td colSpan={7} className="empty-state">No reward achievers match these filters.</td></tr>
                : result.users.map((user) => (
                  <tr key={user._id}>
                    <td><strong>{user.name || "—"}</strong></td>
                    <td>{user.memberId}</td>
                    <td>Club {user.club}</td>
                    <td><span className="status-badge success">{user.rank}</span></td>
                    <td>{user.sponsorId || "—"}</td>
                    <td>{user.phone || "—"}</td>
                    <td><Link className="btn-sm btn-outline" to={`/users/${user._id}`}>View Profile</Link></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {result && !loading && <PaginationBar page={result.pagination.page} limit={filters.limit} total={result.pagination.total} totalPages={result.pagination.totalPages} itemLabel="achievers" onPageChange={(page) => setFilters({ ...filters, page })} onLimitChange={(limit) => setFilters({ ...filters, limit, page: 1 })} />}
      </section>
    </>
  );
};

export default RewardAchievers;
